import React from "react";
import { Navigate, useLocation } from "react-router-dom";
import { useUser } from "../context/UserContext";
import LoadingIcon from "./LoadingIcon";

const ProtectedRoute = ({
  component: Component,
  roles,
  orgTypes,
  envAccess = true,
  componentType = "default",
  requiredPermissions = [],
  superuserOnly = false,
}) => {
  const {
    userData,
    isAuthenticated,
    isAdmin,
    hasOrgPermission,
    isFetchingProfile,
  } = useUser();
  const location = useLocation();

  // Wait for user data to be fetched
  if (isFetchingProfile) {
    return <LoadingIcon height="200px" classname="absolute h-full" />;
  }

  // Check authentication status
  if (!isAuthenticated()) {
    return <Navigate to="/" state={{ from: location }} replace />;
  }

  if (superuserOnly && !userData?.is_superuser) {
    return <Navigate to="/404" replace />;
  }

  const userPermissionNames = new Set(userData.permissions.map((p) => p.name));
  let hasRequiredPermission = true;
  if (requiredPermissions.length > 0) {
    // The user must have at least one of the required permissions
    hasRequiredPermission = requiredPermissions.some((perm) =>
      userPermissionNames.has(perm),
    );
  }

  // Perform role-based and organization-based permission checks
  const hasRolePermission = roles ? isAdmin() : true;
  const hasOrganizationPermission = orgTypes
    ? orgTypes.some((orgType) => hasOrgPermission(orgType))
    : true;

  if (
    (hasRolePermission || userData?.is_superuser) &&
    hasOrganizationPermission &&
    envAccess &&
    (hasRequiredPermission || userData?.is_superuser)
  ) {
    return (
      <>
        {/*
          React 19 hoists this into the document head. It overrides the
          "Phenobee Login | Polybee" title from index.html once someone is
          signed in, so a bookmarked dashboard does not read "Login".
          Deliberately the only component that renders a title: React's docs
          say two titles rendered at the same time is undefined behaviour.
        */}
        <title>Phenobee</title>
        <Component componentType={componentType} />
      </>
    );
  }

  // Redirect to 404 if permissions are not satisfied
  return <Navigate to="/404" replace />;
};

export default ProtectedRoute;
